/**
 * @fileoverview Componente Molecula para mostrar el indicador visual de consolidación de mensajes.
 * @module ConsolidationIndicatorMC
 * @category Component - Molecule
 */

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * Indicador visual profesional con diseño glassmorphism y UX optimizada
 * para mostrar el estado de consolidación de mensajes del agente.
 */
const ConsolidationIndicatorMC = memo(({
  isConsolidating,
  timeLeft,
  phraseCount,
  charCount,
  consolidatedText = ''
}) => {
  const { t } = useTranslation('InboxMC');

  if (!isConsolidating) return null;

  const phraseLabel = phraseCount === 1
    ? t('consolidation.phrase')
    : t('consolidation.phrases');

  return (
    <div className="tw-flex tw-justify-end tw-w-full tw-mb-3 tw-px-2">
      <div className="tw-relative tw-max-w-[75%] tw-bg-emerald-50 tw-border tw-border-emerald-300/80 tw-rounded-2xl tw-rounded-tr-xs tw-p-3 tw-shadow-xs tw-transition-all tw-duration-300">
        {/* Header Indicator inside Outbound Bubble */}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-pb-1.5 tw-mb-1.5 tw-border-b tw-border-emerald-200/70">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-relative tw-flex tw-h-2 tw-w-2">
              <span className="tw-animate-ping tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-emerald-500 tw-opacity-75" />
              <span className="tw-relative tw-inline-flex tw-rounded-full tw-h-2 tw-w-2 tw-bg-emerald-600" />
            </span>
            <span className="tw-text-[11px] tw-font-semibold tw-text-emerald-900">
              {t('consolidation.title')}
            </span>
            <span className="tw-text-[10px] tw-font-medium tw-px-1.5 tw-py-0.2 tw-rounded-full tw-bg-emerald-100 tw-text-emerald-800 tw-border tw-border-emerald-200">
              {phraseCount} {phraseLabel}
            </span>
          </div>

          <div className="tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-text-[10px] tw-font-mono tw-font-bold tw-bg-emerald-600 tw-text-white tw-px-2 tw-py-0.5 tw-rounded-full tw-shadow-2xs">
              {timeLeft}s
            </span>
          </div>
        </div>

        {/* Live Consolidated Message Content */}
        <div className="tw-text-gray-900 tw-text-xs tw-leading-relaxed tw-whitespace-pre-wrap tw-break-words">
          {consolidatedText}
        </div>

        {/* Footer Status */}
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-1 tw-mt-1.5 tw-text-[10px] tw-text-emerald-700 tw-font-mono tw-font-medium">
          <span>{charCount}/200 {t('consolidation.chars_accumulated')}</span>
        </div>
      </div>
    </div>
  );
});

ConsolidationIndicatorMC.displayName = 'ConsolidationIndicatorMC';

ConsolidationIndicatorMC.propTypes = {
  isConsolidating: PropTypes.bool.isRequired,
  timeLeft: PropTypes.number.isRequired,
  phraseCount: PropTypes.number.isRequired,
  charCount: PropTypes.number.isRequired,
  consolidatedText: PropTypes.string
};

export default ConsolidationIndicatorMC;
